import { defineStore } from 'pinia';
import { getTravelAssistant, useDpStore } from './dp';
import { DialogListConfig, ModuleListConfig, ModuleName } from '@/types/module-type';
import { AREA_CONF, DEFAULT_HEADER_CONFIG } from '@/config';
import { useLocationStore } from './location';
import { formatAreaName, getWujiconfig, formatBannerData, formatCardInfoByLang, formatHeaderLogo  } from '@/business';
import { AreaHeaderInfo, AreaKey, AreaLocalConfigWithHandler, RecommendList } from '@/types/area';
import { ScanGiftInfo } from '@/types';
import { DpItem } from '@tencent/wphk-components-dp-loader/src/types';
import { MAINLAND_COMMON_BANNER_POSID, MAINLAND_MERCHANT_RECOMMEND_POSID_LIST } from '@/config/module-config/mainland';
import { getServerTime } from '@tencent/wphk-components-common/utils/server-time';
import dayjs from 'dayjs';
import { Banner, ColumnLayout, ColumnType } from '@/types/banner-layout';
type ConfigState = {
  /** 本地区域配置 */
  areaConfig: AreaLocalConfigWithHandler;
  /** 区域无极配置 */
  wujiConfig: AreaHeaderInfo & any;
};

export const useConfigStore = defineStore('config', {
  state: (): ConfigState => ({
    areaConfig: getAreaConfig(),
    wujiConfig: {},
  }),
  getters: {
    /** 当前区域配置 */
    currentAreaConfig(state) {
      return state.areaConfig[useLocationStore().currentArea];
    },
    /** 当前区域模块配置 */
    currentAreaModuleList(): ModuleListConfig {
      return (this.currentAreaConfig?.moduleListConfig || []).sort((a, b) => a.sort - b.sort);
    },
    currentAreaDialogList(): DialogListConfig {
      return (this.currentAreaConfig?.dialogListConfig || []);
    },
    /** 当前区域所有投放位置配置 */
    currentAreaAllDpPos(): string[] {
      return this.currentAreaModuleList
        .filter(module => module.extraConfig?.modulePositionId)
        .flatMap(module => module.extraConfig?.modulePositionId) as string[];
    },
    /** 当前区域微信分享配置 */
    currentAreaWxShareConf(state) {
      return state.areaConfig[useLocationStore().currentArea].wxShareConf;
    },
    /** 当前区域推荐其他区域配置 */
    currentAreaRecommendAreaList(state) {
      const list: RecommendList[] = [];
      for (const [key, value] of Object.entries(state.areaConfig)) {
        if (key !== useLocationStore().currentArea) {
          list.push({
            key: key as AreaKey,
            img: value.recommendImg,
          });
        }
      }
      return list;
    },
    /** 所有区域名字配置 */
    areaNameList(state) {
      const nameLsit: { name: string; key: AreaKey }[] = [];
      for (const [key, area] of Object.entries(state.areaConfig)) {
        nameLsit.push({ name: area.name, key: key as AreaKey });
      }
      return nameLsit;
    },
    /** 所有区域Key配置 */
    areaKeyList(state) {
      return Object.keys(state.areaConfig);
    },
  },
  actions: {
    /** 获取当前区域模块的额外配置 */
    getExtraConfig(moduleName: ModuleName) {
      return this.currentAreaModuleList.find(module => module.moduleName === moduleName)?.extraConfig;
    },
    /** 获取模块对应的配置 */
    async getConfig(moduleName: ModuleName) {
      // 模块的额外配置
      const extraConfig = this.getExtraConfig(moduleName);
      // 获取投放中台配置
      const dpData = extraConfig?.modulePositionId
        ? await useDpStore().filterDpDataByPosition({ dpPositionIdList: extraConfig?.modulePositionId })
        : [];
      // 处理各个模块的配置
      return this.currentAreaConfig?.moduleHandler(moduleName, dpData, extraConfig);
    },
    /** 获取当前区域 wuji 配置 */
    async getCurrentWujiConfig() {
      const { currentArea } = useLocationStore();
      if (!this.wujiConfig[currentArea]) {
        // 获取总无极配置
        const { wujiConfigUrl } = this.currentAreaConfig;
        this.wujiConfig[currentArea] = await getWujiconfig(wujiConfigUrl);
      }
      return this.wujiConfig[currentArea]?.[0];
    },
    /** 获取当前区域头部信息配置 */
    async getCurrentAreaHeaderConfig() {
      const currentWujiConfig = await this.getCurrentWujiConfig();
      return {
        areaKey: currentWujiConfig?.areaKey || DEFAULT_HEADER_CONFIG.areaKey,
        areaName: formatAreaName(currentWujiConfig?.areaName) || DEFAULT_HEADER_CONFIG.areaName,
        headerLogo: formatHeaderLogo(currentWujiConfig) || DEFAULT_HEADER_CONFIG.headerLogo,
      };
    },
    /** 获取资讯专区和新人资讯的布局信息 */
    async getBannerConfig() {
      const currentWujiConfig = await this.getCurrentWujiConfig();

      const validateTime =  (bannerListData: Banner[]) => {
        const now = getServerTime();
        const bannerlistWithinTime =   bannerListData.filter((item) => {
          const startTime = dayjs(item.startTime);
          const endTime = dayjs(item.endTime);
          return dayjs(startTime).isBefore(now) && dayjs(endTime).isAfter(now);
        });
        return bannerlistWithinTime;
      };

      const checkColumnLayout = (bannerListData: Banner[], type: ColumnType): boolean => {
        if (bannerListData.length === 0) return false;
        switch (type) {
          case ColumnType.a:
            return bannerListData.length <= 3;
          case ColumnType.b:
            return bannerListData.length === 3;
          case ColumnType.c:
            return bannerListData.length % 2 === 0 && bannerListData.length <= 6;
          case ColumnType.d:
            return bannerListData.length === 3 || bannerListData.length === 2;
          default:
            return false;
        }
      };

      const formatBannerList = (layoutList: ColumnLayout[]) => layoutList?.map(layoutItem => ({ ...layoutItem,
        bannerlist: validateTime(layoutItem.bannerlist),
      })).filter(layoutItem => checkColumnLayout(layoutItem.bannerlist, layoutItem.columnType));

      return formatBannerList(currentWujiConfig?.travelLayout || []);
    },
    /** 获取过滤后的布局信息 */
    async getFilteredLayout(includedColumnTypes: ColumnType[]) {
      const bannerConfig = await this.getBannerConfig();
      const filteredLayoutInfo = bannerConfig.
        filter(layoutItem => includedColumnTypes.includes(layoutItem.columnType));
      return filteredLayoutInfo;
    },
    /** 获取内地迎新奖赏商户列表图url地址 */
    async getScanGiftInfo(): Promise<ScanGiftInfo | null> {
      const { scanGiftList = [] } = await this.getCurrentWujiConfig() || {};
      const [scanGift] = scanGiftList;
      if (scanGift?.spId && scanGift?.campaignId) {
        const { banner, bannerDetail, spId, campaignId, startTime, endTime, bannerJumpUrl } = scanGift;
        return {
          banner,
          bannerDetail,
          bannerJumpUrl,
          spId,
          campaignId,
          startTime,
          endTime,
        };
      }
      return null;
    },
  },
});

// 获取区域配置,扩展静态配置AREA_CONF，补充模块处理函数moduleHandler
function getAreaConfig(): AreaLocalConfigWithHandler {
  const obj = {};
  for (const [key, area] of Object.entries(AREA_CONF)) {
    switch (key) {
      case AreaKey.MainLand:
        obj[key] = { ...area, moduleHandler: mainlandFormatModuleConf };
        break;
      case AreaKey.Macao:
        obj[key] = { ...area, moduleHandler: macaoFormatModuleConf };
        break;
      case AreaKey.Japan:
        obj[key] = { ...area, moduleHandler: japanFormatModuleConf };
        break;
      default:
        break;
    }
  }
  // @ts-ignore
  return obj;
}

/**
 * 中国内地-模块处理函数
 * @param moduleName 模块名
 * @param dpData 投放数据
 * @param extraConfig 本地额外配置
 * @returns
 */
async function mainlandFormatModuleConf(moduleName: ModuleName, dpData: DpItem[] = [], extraConfig) {
  switch (moduleName) {
    case ModuleName.Recommend: {

    }
    case ModuleName.MerchantBanner: {
      /** 北上精选推荐位布局列表 */
      const filteredMerchantBannerLayoutList = dpData
        .filter(item => MAINLAND_MERCHANT_RECOMMEND_POSID_LIST.includes(item.dpPosition));

      /** 北上精选banner轮播图列表 */
      const bannerList = dpData.filter(item => item.dpPosition === MAINLAND_COMMON_BANNER_POSID).map(formatBannerData);

      return { bannerList, filteredMerchantBannerLayoutList };
    }
    // 热门商户
    case ModuleName.HotMerchant: {
      const dpStore = useDpStore();
      await Promise.all([dpStore.initWujiScrollMerchant(), dpStore.initWujiRecommendMerchant()]);
      const { merchantDpList, mainlainScrollMerchantList } = dpStore;
      return {
        mainlainScrollMerchantList,
        merchantDpList,
      };
    }
    // 城市攻略
    case ModuleName.CityStrategy: {
      return dpData?.map(formatBannerData);
    }
    default: {
      break;
    }
  }
}
/**
 * 中国澳门-模块处理函数
 * @param moduleName 模块名
 * @param dpData 投放数据
 * @param extraConfig 本地额外配置
 * @returns
 */
async function macaoFormatModuleConf(moduleName: ModuleName, dpData, extraConfig) {
  switch (moduleName) {
    // 旅行好助手
    case ModuleName.TravelAssistant: {
      const travelTools = await getTravelAssistant();
      const bannerList = dpData
        ?.filter(ele => ele.dpPosition === extraConfig.modulePositionId[0])
        ?.map(formatBannerData);
      return { bannerList, travelTools };
    }
    // 热门商户
    case ModuleName.HotMerchant: {
      const dpStore = useDpStore();
      await dpStore.initWujiRecommendMerchant();
      const { merchantDpList } = dpStore;
      return {
        merchantDpList,
      };
    }
    default: {
      break;
    }
  }
}
/**
 * 日本-模块处理函数
 * @param moduleName 模块名
 * @param dpData 投放数据
 * @param extraConfig 本地额外配置
 * @returns
 */
async function japanFormatModuleConf(moduleName: ModuleName, dpData, extraConfig) {
  switch (moduleName) {
    // 出行推荐
    case ModuleName.TravelRecommend: {
      return { list: dpData.map(ele => formatCardInfoByLang(ele.contentBaseData)) };
    }
    // 热门商户
    case ModuleName.HotMerchant: {
      const dpStore = useDpStore();
      await dpStore.initWujiRecommendMerchant();
      const { merchantDpList } = dpStore;
      return {
        merchantDpList,
      };
    }
    default: {
      break;
    }
  }
}
